import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
// import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import "./playout-tour-theme.css";

const PlayoutTourContext = createContext({
    startPlayoutTour: () => { },
    stopPlayoutTour: () => { },
    isPlayoutTourRunning: false,
    currentStep: 0,
    totalSteps: 9,
});

export const usePlayoutTour = () => useContext(PlayoutTourContext);

// Tour steps configuration
const PLAYOUT_TOUR_STEPS = [
    {
        id: 'create-channel',
        route: '/tv-playouts',
        target: '#playout-create-btn',
        title: 'Create Channel',
        text: `<div class="playout-tour-content">
      <p>Start by creating a new playout channel.</p>
      <div class="tour-hand-gesture">
        <span class="tour-hand-icon">👆</span>
        <span class="tour-hand-text">Click Create</span>
      </div>
    </div>`,
        placement: 'bottom'
    },
    {
        id: 'add-rtmp',
        route: null,
        target: '#video-category-add-button',
        title: 'Configure RTMP',
        text: `<div class="playout-tour-content"><p>Create a new RTMP destination for your broadcast.</p></div>`,
        placement: 'bottom'
    },
    {
        id: 'select-destination',
        route: null,
        target: '#playout-rtmp-selector',
        title: 'Select Destination',
        text: `<div class="playout-tour-content"><p>Select your RTMP Destination from this list.</p></div>`,
        placement: 'bottom'
    },
    {
        id: 'status-settings',
        route: null,
        target: '#playout-status-settings',
        title: 'Status & Retention',
        text: `<div class="playout-tour-content"><p>Enable "Status" to activate the channel and "Retain" to save history.</p></div>`,
        placement: 'bottom'
    },
    {
        id: 'video-upload',
        route: null,
        target: '#playout-add-content-btns',
        title: 'Add Program',
        text: `<div class="playout-tour-content"><p>Upload new videos to your library or add a custom program (URL) to your schedule.</p></div>`,
        placement: 'left'
    },
    {
        id: 'add-vod-video',
        route: null,
        target: '#playout-add-vod-btn-0',
        title: 'Add to Schedule',
        text: `<div class="playout-tour-content"><p>Click the + icon to add this video to your schedule.</p></div>`,
        placement: 'left',
    },
    {
        id: 'set-duration',
        route: null,
        target: '#playout-duration-input-0',
        title: 'Set Duration',
        text: `<div class="playout-tour-content"><p>Verify or adjust the duration for this program.</p></div>`,
        placement: 'left'
    },
    {
        id: 'schedule-start-time',
        route: null,
        target: '#Schedule-date',
        title: 'Schedule Start Time',
        text: `<div class="playout-tour-content"><p>Set when your channel begins streaming.</p></div>`,
        placement: 'right'
    },
    {
        id: 'set-schedule',
        route: null,
        target: '#playout-set-schedule-btn',
        title: 'Set Schedule',
        text: `<div class="playout-tour-content"><p>Finally, click "Set Schedule" to publish your playout and start the broadcast.</p></div>`,
        placement: 'top'
    },
];

export const PlayoutTourProvider = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [running, setRunning] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const tourRef = useRef(null);
    const tourTimeoutRef = useRef(null);
    const cancelledByNav = useRef(false);
    const closingRef = useRef(false);
    const finishingRef = useRef(false);
    const vodClickListenerRef = useRef(null);

    const totalSteps = PLAYOUT_TOUR_STEPS.length;

    const waitForTarget = useCallback(async (selector, timeout = 8000) => {
        const start = performance.now();
        return await new Promise((resolve) => {
            const loop = () => {
                const el = document.querySelector(selector);
                if (el) return resolve(true);
                if (performance.now() - start > timeout) return resolve(false);
                requestAnimationFrame(loop);
            };
            loop();
        });
    }, []);

    const disposeTour = useCallback(() => {
        // Prevent recursion
        if (closingRef.current) return;
        closingRef.current = true;

        if (tourTimeoutRef.current) {
            clearTimeout(tourTimeoutRef.current);
            tourTimeoutRef.current = null;
        }

        // Cleanup manual event listeners
        if (vodClickListenerRef.current) {
            const el = document.querySelector('#playout-add-vod-btn-0');
            if (el) el.removeEventListener('click', vodClickListenerRef.current);
            vodClickListenerRef.current = null;
        }

        // Global Shepherd cleanup to prevent duplicates
        import('shepherd.js').then((ShepherdModule) => {
            const Shepherd = ShepherdModule.default || ShepherdModule;
            if (Shepherd.activeTour) {
                try {
                    Shepherd.activeTour.cancel();
                } catch (e) { }
            }
        }).catch(() => { });

        if (tourRef.current) {
            try {
                tourRef.current.cancel();
                tourRef.current.destroy(); // Force destroy
            } catch (e) { console.warn("Tour destroy error", e); }
            tourRef.current = null;
        }

        // Brute force DOM cleanup of any stray tour elements
        document.querySelectorAll('.shepherd-element, .shepherd-modal-overlay-container').forEach(el => el.remove());

        closingRef.current = false;
    }, []);

    const getCookieKey = useCallback(() => {
        const username = localStorage.getItem("username");
        return username ? `playout_tour_seen_${username.replace(/[^a-zA-Z0-9]/g, '_')}` : 'playout_tour_seen';
    }, []);

    const finish = useCallback(() => {
        if (finishingRef.current) return;
        finishingRef.current = true;

        // Clear persistence FIRST to prevent useEffect from re-triggering
        localStorage.removeItem('playout_tour_active');
        localStorage.removeItem('playout_tour_step');

        // Mark as seen permanently in cookies
        Cookies.set(getCookieKey(), 'true', { expires: 365, path: '/' });

        disposeTour();
        setRunning(false);
        setCurrentStepIndex(0);

        // Reset the finishing flag after a short delay
        setTimeout(() => {
            finishingRef.current = false;
        }, 800);
    }, [disposeTour, getCookieKey]);

    const createStepButtons = (idx, stepId) => {
        const buttons = [];

        // Skip button (always available)
        buttons.push({
            text: 'Skip',
            action: () => finish(),
            classes: 'playout-btn-skip',
        });

        // Back button (not on first step)
        if (idx > 0) {
            buttons.push({
                text: 'Back',
                action: () => {
                    cancelledByNav.current = true;
                    if (tourRef.current) tourRef.current.cancel();
                    showStep(idx - 1);
                },
                classes: 'playout-btn-secondary',
            });
        }

        // Next/Finish button
        if (idx < totalSteps - 1) {
            if (idx === 0) {
                // Step 1: "Create Your Channel" - No Next button, wait for click
                buttons.push({
                    text: 'Wait for Create...',
                    action: () => { },
                    classes: 'playout-btn-secondary disabled style-disabled',
                    disabled: true
                });
            } else if (stepId === 'add-vod-video') {
                // Step: "Add VOD" - Force user to click the + button
                buttons.push({
                    text: 'Click (+) to Add',
                    action: () => { },
                    classes: 'playout-btn-secondary disabled style-disabled',
                    disabled: true
                });
            } else {
                buttons.push({
                    text: 'Next',
                    action: () => {
                        cancelledByNav.current = true;
                        if (tourRef.current) tourRef.current.cancel();
                        showStep(idx + 1);
                    },
                    classes: 'playout-btn-primary',
                });
            }
        } else {
            buttons.push({
                text: 'Finish',
                action: () => finish(),
                classes: 'playout-btn-primary',
            });
        }

        return buttons;
    };

    const createProgressDots = (currentIdx) => {
        return `<div class="playout-tour-progress">
      <div class="dots-container">
        ${PLAYOUT_TOUR_STEPS.map((_, i) =>
            `<span class="progress-dot ${i === currentIdx ? 'active' : ''} ${i < currentIdx ? 'completed' : ''}"></span>`
        ).join('')}
      </div>
    </div>`;
    };

    const showStep = useCallback(async (idx) => {
        // Add a small delay for later steps to ensure previous step cleanup and DOM settling
        if (idx > 0) await new Promise(r => setTimeout(r, 400));

        setCurrentStepIndex(idx);
        localStorage.setItem('playout_tour_step', idx.toString());

        const step = PLAYOUT_TOUR_STEPS[idx];
        if (!step) return finish();

        // Navigate if needed
        if (step.route && location.pathname !== step.route) {
            navigate(step.route);
            // Return here to allow useEffect to pick up the new location and resume
            // This prevents duplicate tours: one from here, one from useEffect
            return;
        }

        const ok = await waitForTarget(step.target, 12000);
        if (!ok) {
            console.warn(`Playout Tour: Target "${step.target}" not found on ${location.pathname}`);
            if (idx === 0) {
                setTimeout(() => finish(), 1000);
            }
            return;
        }

        // Ensure any existing tour is fully removed before creating a new one
        disposeTour();

        // Manual listener for VOD add step
        if (step.id === 'add-vod-video') {
            const el = document.querySelector(step.target);
            if (el) {
                const handler = () => {
                    // Wait for React state update and DOM render
                    setTimeout(() => {
                        vodClickListenerRef.current = null;
                        showStep(idx + 1);
                    }, 800);
                };
                vodClickListenerRef.current = handler;
                el.addEventListener('click', handler, { once: true });
            }
        }

        const ShepherdModule = await import('shepherd.js');
        const Shepherd = ShepherdModule.default || ShepherdModule;

        const tour = new Shepherd.Tour({
            defaultStepOptions: {
                classes: 'playout-tour-step playout-tour-v2',
                scrollTo: {
                    behavior: 'smooth',
                    block: 'center'
                },
                modalOverlayOpeningPadding: 8,
                modalOverlayOpeningRadius: 8,
            },
            useModalOverlay: true,
        });

        tour.addStep({
            id: step.id,
            title: `<div class="playout-tour-title">${step.title}</div>`,
            text: step.text + `<div class="tour-footer-indicators">` + createProgressDots(idx) + `<span class="tour-step-counter-bottom">Step ${idx + 1} of ${totalSteps}</span></div>`,
            attachTo: { element: step.target, on: step.placement || 'bottom' },
            buttons: createStepButtons(idx, step.id),
            advanceOn: idx === 0 ? { selector: '#playout-create-btn', event: 'click' } : undefined,
        });

        tour.on('cancel', () => {
            if (closingRef.current) {
                cancelledByNav.current = false;
                return;
            }
            if (!cancelledByNav.current) finish();
            cancelledByNav.current = false;
        });

        tourRef.current = tour;

        // Double check cancellation before starting
        if (localStorage.getItem('playout_tour_active') !== 'true' && idx !== 0) {
            // If we're not step 0 (start), and not active, don't start.
            // Exceptions: idx 0 starts the tour.
        }

        tour.start();
    }, [finish, navigate, location.pathname, waitForTarget, totalSteps]);

    const startPlayoutTour = useCallback(() => {
        disposeTour();
        localStorage.setItem('playout_tour_active', 'true');
        localStorage.setItem('playout_tour_step', '0');

        // Mark as seen immediately so it doesn't auto-trigger again if page is refreshed mid-tour
        Cookies.set(getCookieKey(), 'true', { expires: 365, path: '/' });

        setRunning(true);
        showStep(0);
    }, [showStep, disposeTour, getCookieKey]);

    const stopPlayoutTour = useCallback(() => {
        finish();
    }, [finish]);

    // Resume tour if it was active (e.g., after page navigation)
    useEffect(() => {
        const isActive = localStorage.getItem('playout_tour_active') === 'true';
        const savedStep = parseInt(localStorage.getItem('playout_tour_step') || '0', 10);

        if (isActive && !running && !finishingRef.current) {
            if (tourTimeoutRef.current) clearTimeout(tourTimeoutRef.current);

            // TV Playouts page - Step 1
            if (location.pathname === '/tv-playouts' && savedStep === 0) {
                setRunning(true);
                tourTimeoutRef.current = setTimeout(() => showStep(0), 500);
            }
            // Edit page - Step 2+ (user just created a channel)
            else if (location.pathname.includes('/edit/tv-playouts/') && savedStep <= 1) {
                setRunning(true);
                // Resume from Step 2 (RTMP) if they just came from Step 1 (Create Channel)
                tourTimeoutRef.current = setTimeout(() => showStep(1), 500);
            } else if (location.pathname.includes('/edit/tv-playouts/') && savedStep > 1) {
                // Resume whatever step they were on
                setRunning(true);
                tourTimeoutRef.current = setTimeout(() => showStep(savedStep), 500);
            }
        } else if (!isActive && !running && !finishingRef.current && location.pathname === '/tv-playouts') {
            // Auto-trigger for first-time users
            const isSeen = Cookies.get(getCookieKey()) === 'true';
            if (!isSeen) {
                startPlayoutTour();
            }
        }
    }, [location.pathname, running, showStep, startPlayoutTour, getCookieKey]);

    // Cleanup on unmount
    useEffect(() => () => disposeTour(), []);

    const value = useMemo(() => ({
        startPlayoutTour,
        stopPlayoutTour,
        isPlayoutTourRunning: running,
        currentStep: currentStepIndex,
        totalSteps,
    }), [startPlayoutTour, stopPlayoutTour, running, currentStepIndex, totalSteps]);

    return (
        <PlayoutTourContext.Provider value={value}>
            {children}
        </PlayoutTourContext.Provider>
    );
};

export default PlayoutTourProvider;
